import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Crosshair, Globe, House, MagnifyingGlass } from 'phosphor-react';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { clearGuestVacationLocation, readGuestVacationLocation, writeGuestVacationLocation } from '@/lib/guestLocation';
import { cn } from '@/lib/cn';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { PageHeader } from '@/components/ui/PageHeader';
import { GoldenRule } from '@/components/ui/GoldenRule';

type City = { display_name: string; lat: string; lon: string };

const SAVE_ERR = "Impossible d'enregistrer ce lieu. Vérifiez votre connexion et réessayez.";

/** Ligne de choix de lieu (résidence / lieu personnalisé), avec état sélectionné. */
function ChoiceRow({ selected, icon, title, subtitle, onSelect }: {
  selected: boolean; icon: ReactNode; title: string; subtitle?: string | null; onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={selected ? undefined : onSelect}
      className={cn(
        'w-full flex items-center gap-3 rounded-md p-4 text-left transition',
        selected ? 'bg-paper-elev ring-2 ring-terre' : 'bg-paper-elev ring-1 ring-paper-edge hover:bg-paper-rec',
      )}
    >
      <span className={cn('shrink-0', selected ? 'text-terre' : 'text-ink-3')}>{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block t-ui">{title}</span>
        {subtitle ? <span className="block t-ui-xs text-ink-3 truncate">{subtitle}</span> : null}
      </span>
      {selected ? <Check size={18} weight="bold" className="text-terre shrink-0" /> : null}
    </button>
  );
}

export default function Vacation() {
  const { user, profile, refreshProfile } = useAuth();
  const nav = useNavigate();
  const guestLocation = readGuestVacationLocation();
  const [q, setQ] = useState('');
  const [list, setList] = useState<City[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Le lieu réellement utilisé se décide entre deux options explicites :
  //  - le lieu de repli : ville de résidence (connecté) ou position par défaut (invité) ;
  //  - un lieu personnalisé, toujours visible même s'il est momentanément inactif.
  const homeLabel = user ? (profile?.home_city ?? null) : null;
  const customLabel = user ? (profile?.vacation_label ?? null) : (guestLocation?.label ?? null);
  const customActive = user ? !!profile?.vacation_active : !!guestLocation?.active;

  async function search() {
    if (!q.trim()) return;
    setBusy(true);
    setError(null);
    const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&countrycodes=fr&limit=5&q=${encodeURIComponent(q)}`,
      { headers: { 'Accept-Language': 'fr' } });
    const d = await r.json(); setList(d); setBusy(false);
  }

  async function useCurrentPosition() {
    setBusy(true);
    setError(null);
    try {
      let lat: number;
      let lon: number;

      if (Capacitor.isNativePlatform()) {
        await Geolocation.requestPermissions();
        const pos = await Geolocation.getCurrentPosition({ timeout: 5000, maximumAge: 300_000, enableHighAccuracy: false });
        lat = pos.coords.latitude;
        lon = pos.coords.longitude;
      } else {
        if (!navigator.geolocation) { setBusy(false); return; }
        const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000, maximumAge: 300_000, enableHighAccuracy: false })
        );
        lat = pos.coords.latitude;
        lon = pos.coords.longitude;
      }

      const nextLocation = { active: true, label: 'Ma position', lat, lon };

      if (!user) {
        writeGuestVacationLocation(nextLocation);
        setBusy(false);
        nav('/decouvrir');
        return;
      }

      const { error: upErr } = await supabase.from('profiles').update({
        vacation_active: true,
        vacation_label: nextLocation.label,
        vacation_lat: nextLocation.lat,
        vacation_lon: nextLocation.lon,
      }).eq('id', user.id);
      if (upErr) { setBusy(false); setError(SAVE_ERR); return; }
      await refreshProfile();
      setBusy(false);
      nav('/decouvrir');
    } catch {
      setBusy(false);
    }
  }

  async function pick(c: City) {
    const nextLocation = {
      active: true,
      label: c.display_name.split(',')[0],
      lat: parseFloat(c.lat),
      lon: parseFloat(c.lon),
    };
    setError(null);

    if (!user) {
      writeGuestVacationLocation(nextLocation);
      nav('/decouvrir');
      return;
    }

    const { error: upErr } = await supabase.from('profiles').update({
      vacation_active: true, vacation_label: nextLocation.label,
      vacation_lat: nextLocation.lat, vacation_lon: nextLocation.lon,
    }).eq('id', user.id);
    if (upErr) { setError(SAVE_ERR); return; }
    await refreshProfile(); nav('/decouvrir');
  }

  // Revenir au lieu de repli (résidence connecté / position par défaut invité).
  async function selectFallback() {
    setError(null);
    if (!user) { clearGuestVacationLocation(); nav('/decouvrir'); return; }
    const { error: upErr } = await supabase.from('profiles').update({ vacation_active: false }).eq('id', user.id);
    if (upErr) { setError(SAVE_ERR); return; }
    await refreshProfile(); nav('/decouvrir');
  }

  // Réactiver un lieu personnalisé déjà enregistré mais désactivé.
  async function activateCustom() {
    setError(null);
    if (!user) {
      const guest = readGuestVacationLocation();
      if (guest) writeGuestVacationLocation({ ...guest, active: true });
      nav('/decouvrir'); return;
    }
    const { error: upErr } = await supabase.from('profiles').update({ vacation_active: true }).eq('id', user.id);
    if (upErr) { setError(SAVE_ERR); return; }
    await refreshProfile(); nav('/decouvrir');
  }

  return (
    <div className="bg-paper text-ink-1 min-h-screen pb-tabbar">
      <PageHeader back eyebrow="Choisir un" title="Lieu" italicWord="lieu" />

      <section className="px-5 mb-8">
        <div className="t-cap text-ink-3 mb-3">Les sorties s'affichent autour de</div>
        <div className="space-y-2">
          {/* Lieu de repli : résidence (connecté) ou position par défaut (invité). */}
          {(homeLabel || !user) && (
            <ChoiceRow
              selected={!customActive}
              icon={<House size={18} />}
              title={user ? 'Ma ville de résidence' : 'Ma position habituelle'}
              subtitle={homeLabel ?? 'Autour de vous'}
              onSelect={selectFallback}
            />
          )}
          {/* Lieu personnalisé : toujours affiché, même inactif — on le voit et on peut le réactiver. */}
          {customLabel && (
            <ChoiceRow
              selected={customActive}
              icon={<Globe size={18} />}
              title="Un autre lieu"
              subtitle={customLabel}
              onSelect={activateCustom}
            />
          )}
        </div>
        {error ? <p role="alert" className="mt-3 t-ui-xs text-terre">{error}</p> : null}
      </section>

      <section className="px-5">
        <div className="flex items-center gap-3 mb-4"><GoldenRule /><span className="t-cap whitespace-nowrap text-ink-3">{customLabel ? 'Changer de lieu' : 'Choisir un lieu'}</span><GoldenRule /></div>
        <div className="flex gap-2">
          <Input placeholder="Ex. Saint-Malo" value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && search()} />
          <Button onClick={search} loading={busy} iconLeft={<MagnifyingGlass size={16} />}>Chercher</Button>
        </div>
        <div className="mt-3">
          <Button variant="secondary" full onClick={useCurrentPosition} loading={busy} iconLeft={<Crosshair size={16} />}>
            Utiliser ma position actuelle
          </Button>
        </div>
        <ul className="mt-4 divide-y divide-paper-edge">
          {list.map((c, i) => (
            <li key={`${i}-${c.display_name}`}>
              <button onClick={() => pick(c)} className="w-full text-left py-3">
                <div className="t-ed-md">{c.display_name.split(',')[0]}</div>
                <div className="t-ui-xs text-ink-3 truncate">{c.display_name}</div>
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
